import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/locale";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import {
  Input,
} from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Flame, Shield, Car, Receipt, BarChart3, Download, RotateCcw, Camera, Loader2, Repeat, Check, X as XIcon, Globe } from "lucide-react";
import { toast } from "sonner";
import OnlineSubscriptions from "@/components/OnlineSubscriptions";

const SOURCE_META = {
  bill:         { label: "Bill",         icon: Flame,  chip: "bg-hearth-clay/12 text-hearth-clayDark" },
  insurance:    { label: "Insurance",    icon: Shield, chip: "bg-hearth-ink/10 text-hearth-ink" },
  vehicle:      { label: "Vehicle",      icon: Car,    chip: "bg-hearth-slate/12 text-hearth-slate" },
  subscription: { label: "Subscription", icon: Globe,  chip: "bg-hearth-sage/15 text-hearth-sageDark" },
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Soft, warm avatar tints — chosen deterministically per member id so the
// same person always gets the same colour across the app.
const AVATAR_TINTS = [
  { bg: "#EFE1D2", fg: "#7A4A20" },  // clay
  { bg: "#DDE7DE", fg: "#3D5A45" },  // sage
  { bg: "#E8DCC2", fg: "#7A5320" },  // amber
  { bg: "#E3DBEA", fg: "#4C3E63" },  // lavender
  { bg: "#DAE2E9", fg: "#3A5063" },  // slate
  { bg: "#EFDCDC", fg: "#8A3E3E" },  // rose
  { bg: "#D9E6E4", fg: "#2F5450" },  // teal
];

function tintFor(id) {
  const key = String(id || "?");
  let h = 0;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}

function MemberAvatar({ member, size = 26 }) {
  const initials = member?.initials || "·";
  const t = tintFor(member?.id || "unknown");
  return (
    <span
      title={member?.name || member?.email || "unknown"}
      data-testid={`exp-avatar-${member?.id || "unknown"}`}
      className="rounded-full inline-grid place-items-center shrink-0 font-mono font-semibold"
      style={{
        width: size, height: size,
        background: t.bg, color: t.fg,
        fontSize: Math.max(9, Math.floor(size * 0.38)),
        letterSpacing: "0.02em",
      }}
    >
      {initials}
    </span>
  );
}

const defaultFrom = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 11);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const defaultTo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
};

export default function Expenses() {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(defaultTo());
  const [category, setCategory] = useState("all");
  const [source, setSource] = useState("all");
  const [paid, setPaid] = useState("all");
  // OCR scan state
  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanCategory, setScanCategory] = useState("Other");
  const scanFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = { from, to };
      if (category !== "all") params.category = category;
      if (source !== "all") params.source = source;
      if (paid !== "all") params.paid = paid;
      const [e, m] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/household/members"),
      ]);
      setData(e.data);
      setMembers(m.data.members || []);
    } catch (err) {
      console.error("Failed to load expenses:", err);
    }
  }, [from, to, category, source, paid]);

  useEffect(() => { load(); }, [load]);

  const memberFor = (id) => members.find((m) => m.id === id);

  const toggleEssential = async (row) => {
    // Optimistic
    const next = !row.essential;
    setData((d) => d ? { ...d, rows: d.rows.map((r) => r.ref_id === row.ref_id && r.source === row.source ? { ...r, essential: next } : r) } : d);
    try {
      await api.post("/expenses/flag", { source: row.source, ref_id: row.ref_id, essential: next });
      // Reload for updated burn rate
      load();
    } catch (e) {
      toast.error("Couldn't save that flag");
      // Revert
      setData((d) => d ? { ...d, rows: d.rows.map((r) => r.ref_id === row.ref_id && r.source === row.source ? { ...r, essential: !next } : r) } : d);
    }
  };

  const runOCR = async (file) => {
    if (!file) return;
    setScanning(true); setScanResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receipts/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScanResult(data);
      setScanCategory(data.suggested_category || "Other");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't read that receipt");
      setScanOpen(false);
    } finally {
      setScanning(false);
    }
  };

  const saveScannedBill = async () => {
    if (!scanResult) return;
    try {
      await api.post("/bills", {
        name: scanResult.merchant || "Receipt",
        amount: scanResult.total || 0,
        category: scanCategory,
        due_date: scanResult.date || null,
        paid: true,
        recurrence: "once",
        note: scanResult.notes ? `Scanned receipt · ${scanResult.notes}` : "Scanned receipt",
      });
      toast.success("Receipt saved to Bills");
      setScanOpen(false); setScanResult(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the bill");
    }
  };

  const categories = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.by_category).sort();
  }, [data]);

  const exportCSV = () => {
    if (!data) return;
    const header = ["date", "source", "category", "description", "amount", "paid"];
    const rows = [header.join(",")];
    for (const r of data.rows) {
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      rows.push([r.date, r.source, r.category, esc(r.description), r.amount.toFixed(2), r.paid ? "yes" : "no"].join(","));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hearthouse-expenses-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setFrom(defaultFrom()); setTo(defaultTo());
    setCategory("all"); setSource("all"); setPaid("all");
  };

  if (!data) return <div className="text-hearth-mute font-display italic text-2xl">Tallying…</div>;

  const max = Math.max(0.01, ...data.monthly.map((m) => m.total));

  return (
    <div>
      <PageHeader
        eyebrow="Money"
        title="Expense sheet"
        subtitle="Every house expense — bills, insurance and vehicle service — in one ledger."
        action={
          <div className="flex gap-2">
            <Button data-testid="exp-scan-btn" onClick={() => { setScanResult(null); setScanOpen(true); }} variant="ghost"
                    className="rounded-full border border-hearth-line hover:bg-hearth-stone text-sm">
              <Camera size={14} className="mr-1.5"/>Scan receipt
            </Button>
            <Button data-testid="exp-reset" onClick={reset} variant="ghost" className="rounded-full text-sm"><RotateCcw size={14} className="mr-1.5"/>Reset</Button>
            <Button data-testid="exp-export" onClick={exportCSV} className="rounded-full bg-hearth-ink hover:bg-hearth-slateDark text-white">
              <Download size={14} className="mr-1.5"/>Export CSV
            </Button>
          </div>
        }
      />

      {/* Burn-rate strip */}
      <section className="warm-card p-5 mb-6 bg-hearth-amber/5 border-hearth-amber/30" data-testid="exp-burn-rate">
        <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
          <div>
            <p className="caption inline-flex items-center gap-1.5"><Repeat size={11} /> Monthly burn rate</p>
            <p className="text-hearth-mute text-xs mt-0.5 italic">Recurring costs, normalised to a month.</p>
          </div>
          <div className="text-right">
            <p className="stat-num text-hearth-clayDark num" data-testid="exp-burn-essential">{money(data.monthly_burn_essential)}</p>
            <p className="caption">Essential · fixed household</p>
          </div>
        </div>
        <div className="mt-2 h-2 rounded-full bg-hearth-stone overflow-hidden">
          {(() => {
            const total = Math.max(0.01, data.monthly_burn_all_recurring);
            const pct = Math.min(100, (data.monthly_burn_essential / total) * 100);
            return (
              <div
                className="h-full bg-hearth-clay transition-all"
                style={{ width: `${pct}%` }}
                data-testid="exp-burn-bar"
              />
            );
          })()}
        </div>
        <div className="flex items-center justify-between mt-1 text-xs text-hearth-mute">
          <span>Essential {money(data.monthly_burn_essential)}</span>
          <span>All recurring {money(data.monthly_burn_all_recurring)}</span>
        </div>
      </section>

      {/* Totals + chart */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-4 warm-card p-6 fade-up">
          <p className="caption">In selected range</p>
          <div className="stat-num text-hearth-ink num mt-2" data-testid="exp-total">{money(data.total)}</div>
          <p className="text-hearth-mute mt-1 text-sm">across <span className="num text-hearth-ink">{data.count}</span> entries</p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="caption">Paid</p>
              <p className="num text-hearth-ink mt-1">{money(data.paid_total)}</p>
            </div>
            <div>
              <p className="caption">Outstanding</p>
              <p className="num text-hearth-coral mt-1">{money(data.unpaid_total)}</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 warm-card p-6 fade-up">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 caption"><BarChart3 size={13} /> Monthly trend</div>
            <span className="caption">last 12 months</span>
          </div>
          <div className="grid grid-cols-12 gap-2 items-end h-40">
            {data.monthly.map((m) => {
              const h = (m.total / max) * 100;
              return (
                <div key={`${m.year}-${m.month}`} className="flex flex-col items-center gap-1.5" data-testid={`exp-bar-${m.year}-${m.month}`}>
                  <div className="w-full flex flex-col items-center justify-end h-full">
                    {m.total > 0 && <div className="text-[9px] font-mono text-hearth-mute mb-0.5">{m.total.toFixed(0)}</div>}
                    <div
                      className="w-full bg-hearth-clay rounded-t-md transition-all"
                      style={{ height: `${Math.max(2, h)}%`, opacity: m.total === 0 ? 0.18 : 1 }}
                    />
                  </div>
                  <div className="text-[10px] text-hearth-mute uppercase tracking-wide">{MONTHS_SHORT[m.month - 1]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="warm-card p-4 sm:p-5 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <Label className="caption">From</Label>
            <Input data-testid="exp-from" type="date" className="input-warm mt-1.5 h-10 w-full min-w-0" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label className="caption">To</Label>
            <Input data-testid="exp-to" type="date" className="input-warm mt-1.5 h-10 w-full min-w-0" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <Label className="caption">Source</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="exp-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="bill">Bills</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="vehicle">Vehicle</SelectItem>
                <SelectItem value="subscription">Online subs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="exp-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption">Status</Label>
            <Select value={paid} onValueChange={setPaid}>
              <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="exp-paid"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Paid</SelectItem>
                <SelectItem value="false">Outstanding</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* By-category chips */}
        {categories.length > 0 && (
          <div className="mt-4 pt-4 border-t border-hearth-line/70 flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button key={c} data-testid={`exp-catchip-${c}`} onClick={() => setCategory(c === category ? "all" : c)}
                className={`chip transition ${c === category ? "bg-hearth-ink text-white" : "bg-hearth-stone text-hearth-ink hover:bg-hearth-line"}`}>
                {c} <span className="font-mono ml-1 opacity-70">{data.by_category[c].toFixed(0)}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Ledger table */}
      {data.rows.length === 0 ? (
        <EmptyState icon={Receipt} title="No expenses in this range." message="Add bills with amounts, insurance premiums, or vehicle service entries to see them here." />
      ) : (
        <div className="warm-card overflow-hidden">
          {/* Horizontal-scroll wrapper — on phones the 12-col grid needs
              a min width so cells don't overlap. The wrapper itself scrolls
              so headers + rows stay aligned. */}
          <div className="overflow-x-auto">
            <div className="min-w-[820px]">
              <div className="grid grid-cols-12 px-5 py-3 caption text-hearth-mute border-b border-hearth-line bg-hearth-stone/40">
                <div className="col-span-1">By</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Source</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-3">Description</div>
                <div className="col-span-1 text-center">Fixed</div>
                <div className="col-span-1 text-right">Amount</div>
              </div>
              <div className="divide-y divide-hearth-line/70" data-testid="exp-table">
            {data.rows.map((r) => {
              const meta = SOURCE_META[r.source] || SOURCE_META.bill;
              const Icon = meta.icon;
              const isRecurring = r.recurrence && r.recurrence !== "once";
              return (
                <div
                  key={r.id}
                  data-testid={`exp-row-${r.id}`}
                  className={`grid grid-cols-12 px-5 py-3 hover:bg-hearth-stone/30 transition items-center text-sm relative ${
                    isRecurring ? "bg-hearth-clay/[0.035]" : ""
                  }`}
                >
                  {isRecurring && (
                    <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-hearth-clay/60 rounded-r" />
                  )}
                  <div className="col-span-1">
                    <MemberAvatar member={memberFor(r.created_by)} />
                  </div>
                  <Link to={r.link} className="col-span-2 font-mono text-hearth-ink hover:underline">{fmtDate(r.date)}</Link>
                  <div className="col-span-2 flex items-center gap-1.5 flex-wrap">
                    <span className={`chip ${meta.chip} inline-flex items-center gap-1`}><Icon size={11} /> {meta.label}</span>
                    {isRecurring && (
                      <span className="chip bg-hearth-clay/12 text-hearth-clayDark inline-flex items-center gap-1" data-testid={`exp-recurring-${r.id}`}>
                        <Repeat size={10} /> {r.recurrence}
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 text-hearth-ink truncate">{r.category}</div>
                  <div className="col-span-3 text-hearth-ink truncate flex items-center gap-2">
                    <span className="truncate">{r.description}</span>
                    {r.paid
                      ? <span className="chip bg-hearth-sage/15 text-hearth-sageDark shrink-0">paid</span>
                      : <span className="chip bg-hearth-coral/15 text-hearth-coral shrink-0">due</span>}
                  </div>
                  <div className="col-span-1 text-center">
                    {isRecurring ? (
                      <button
                        data-testid={`exp-essential-${r.id}`}
                        onClick={() => toggleEssential(r)}
                        title={r.essential ? "Essential (fixed household cost)" : "Optional"}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition ${
                          r.essential
                            ? "bg-hearth-clay text-white"
                            : "bg-hearth-stone text-hearth-mute hover:text-hearth-ink hover:bg-hearth-line"
                        }`}
                      >
                        {r.essential ? "Essential" : "Optional"}
                      </button>
                    ) : (
                      <span className="text-hearth-mute text-[10px]">—</span>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono text-hearth-ink">{money(r.amount)}</div>
                </div>
              );
            })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Online Subscriptions section */}
      <OnlineSubscriptions />

      {/* Receipt OCR dialog */}
      <Dialog open={scanOpen} onOpenChange={(o) => { setScanOpen(o); if (!o) setScanResult(null); }}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold">Scan a receipt</DialogTitle>
          </DialogHeader>
          {!scanResult && !scanning && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-hearth-clay/12 grid place-items-center mb-3">
                <Camera size={26} className="text-hearth-clayDark" />
              </div>
              <p className="text-sm text-hearth-mute mb-5 italic">
                Take or upload a clear photo. We&apos;ll pull the store, total and date, then you confirm the category.
              </p>
              <input
                type="file"
                ref={scanFileRef}
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => { runOCR(e.target.files?.[0]); e.target.value = ""; }}
              />
              <Button
                data-testid="exp-scan-upload"
                onClick={() => scanFileRef.current?.click()}
                className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white"
              >
                <Camera size={14} className="mr-1.5" /> Choose a photo
              </Button>
            </div>
          )}
          {scanning && (
            <div className="text-center py-10">
              <Loader2 size={28} className="mx-auto animate-spin text-hearth-clay" />
              <p className="text-hearth-mute mt-3 font-display italic text-lg">Reading your receipt…</p>
            </div>
          )}
          {scanResult && !scanning && (
            <div className="space-y-4" data-testid="exp-scan-result">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="caption text-hearth-mute">Store</Label>
                  <Input
                    data-testid="exp-scan-merchant"
                    className="input-warm mt-1.5 h-10"
                    value={scanResult.merchant || ""}
                    onChange={(e) => setScanResult({ ...scanResult, merchant: e.target.value })}
                    placeholder="Store name"
                  />
                </div>
                <div>
                  <Label className="caption text-hearth-mute">Total ({scanResult.currency || "AUD"})</Label>
                  <Input
                    data-testid="exp-scan-total"
                    type="number" step="0.01"
                    className="input-warm mt-1.5 h-10 font-mono"
                    value={scanResult.total ?? ""}
                    onChange={(e) => setScanResult({ ...scanResult, total: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label className="caption text-hearth-mute">Date</Label>
                  <Input
                    data-testid="exp-scan-date"
                    type="date"
                    className="input-warm mt-1.5 h-10"
                    value={scanResult.date || ""}
                    onChange={(e) => setScanResult({ ...scanResult, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="caption text-hearth-mute">Category</Label>
                  <Select value={scanCategory} onValueChange={setScanCategory}>
                    <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="exp-scan-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(scanResult.categories || []).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between caption">
                <span className="inline-flex items-center gap-1.5">
                  Confidence:{" "}
                  <span className={`chip ${
                    scanResult.confidence === "high" ? "bg-hearth-sage/15 text-hearth-sageDark" :
                    scanResult.confidence === "low"  ? "bg-hearth-coral/15 text-hearth-coral" :
                    "bg-hearth-amber/20 text-[#7A5320]"
                  }`}>{scanResult.confidence}</span>
                </span>
                {scanResult.notes && <span className="italic text-hearth-mute">{scanResult.notes}</span>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setScanOpen(false)} className="rounded-full">
              <XIcon size={13} className="mr-1" /> Cancel
            </Button>
            {scanResult && !scanning && (
              <Button
                data-testid="exp-scan-save"
                onClick={saveScannedBill}
                className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white"
              >
                <Check size={13} className="mr-1.5" /> Save as bill
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
